import { z } from 'zod'

export const API_CATALOG_METADATA_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.string().optional()

/** Query keys accepted by every metadata catalog read projection. */
export const apiCatalogMetadataReadQuerySchema = z.object({
  accept_fallback: optionalQueryString,
  format: optionalQueryString,
  surface: optionalQueryString,
  feature: optionalQueryString,
  table: optionalQueryString,
  operation: optionalQueryString,
  auth_mode: optionalQueryString,
  stable_key: optionalQueryString,
  q: optionalQueryString,
  limit: optionalQueryString,
  kind: optionalQueryString,
  key: optionalQueryString,
  database: optionalQueryString,
  field: optionalQueryString,
  against: optionalQueryString,
}).passthrough()

const sourceRefSchema = z.object({
  file: z.string().min(1),
}).passthrough()

const fieldImpactSchema = z.object({
  database: z.string().min(1),
  table: z.string().min(1),
  field: z.string().min(1),
  operation: z.string().min(1),
  direction: z.string().min(1),
  meaning: z.string().min(1),
}).passthrough()

const tableImpactSchema = z.object({
  database: z.string().min(1),
  table: z.string().min(1),
  operations: z.array(z.string()),
  detail: z.string().min(1),
  fields: z.array(fieldImpactSchema),
}).passthrough()

export const apiCatalogEndpointSchema = z.object({
  stable_key: z.string().min(1),
  surface: z.string().min(1),
  method: z.string().min(1),
  path_template: z.string().min(1),
  auth_mode: z.string().min(1),
  feature_ids: z.array(z.string()),
  summary: z.string().min(1),
  description: z.string().min(1),
  request_contract: z.string().min(1),
  response_contract: z.string().min(1),
  source_refs: z.array(sourceRefSchema),
  db_impacts: z.array(tableImpactSchema),
}).passthrough()

const surfaceSchema = z.object({
  slug: z.string().min(1),
  display_name: z.string().min(1),
  app_path: z.string().min(1),
}).passthrough()

const featureSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  area: z.string().min(1),
  description: z.string().min(1),
}).passthrough()

const tableSchema = z.object({
  database: z.string().min(1),
  name: z.string().min(1),
  purpose: z.string().min(1),
}).passthrough()

export const apiCatalogSnapshotSchema = z.object({
  contract_version: z.string().min(1),
  catalog_version: z.string().min(1),
  source_commit: z.string().min(1),
  generated_from: z.string().min(1),
  strict_mode: z.boolean(),
  surfaces: z.array(surfaceSchema),
  features: z.array(featureSchema),
  tables: z.array(tableSchema),
  endpoints: z.array(apiCatalogEndpointSchema),
}).passthrough()

const platformMapSchema = z.object({
  apis: z.array(z.record(z.unknown())),
  features: z.array(z.record(z.unknown())),
  tables: z.array(z.record(z.unknown())),
}).passthrough()

const metadataEnvelopeMetaSchema = z.object({
  source: z.enum(['db1', 'bootstrap']),
  redaction: z.string().min(1),
  catalog_version: z.string().min(1),
  source_commit: z.string().min(1),
  generated_from: z.string().min(1),
  strict_mode: z.boolean(),
  reader: z.string().optional(),
  warning: z.string().optional(),
  snapshot_hash: z.string().optional(),
  db_version_id: z.number().optional(),
}).passthrough()

const metadataEnvelope = z.object({
  ok: z.literal(true),
  meta: metadataEnvelopeMetaSchema,
}).passthrough()

export const apiCatalogCatalogResponseSchema = metadataEnvelope.extend({
  data: z.union([apiCatalogSnapshotSchema, platformMapSchema]),
})

export const apiCatalogEndpointDetailResponseSchema = metadataEnvelope.extend({
  data: apiCatalogEndpointSchema,
})

export const apiCatalogTableDetailResponseSchema = metadataEnvelope.extend({
  data: z.object({
    table: tableSchema,
    endpoints: z.array(apiCatalogEndpointSchema),
  }).passthrough(),
})

export const apiCatalogFieldDetailResponseSchema = metadataEnvelope.extend({
  data: z.object({
    database: z.string().min(1),
    table: z.string().min(1),
    field: z.string().min(1),
    endpoints: z.array(apiCatalogEndpointSchema),
  }).passthrough(),
})

export const apiCatalogFeatureTraceResponseSchema = metadataEnvelope.extend({
  data: z.object({
    feature: featureSchema,
    endpoints: z.array(apiCatalogEndpointSchema),
    tables: z.array(tableSchema),
  }).passthrough(),
})

export const apiCatalogSearchResponseSchema = metadataEnvelope.extend({
  data: z.object({
    query: z.string(),
    results: z.array(z.record(z.unknown())),
    source_ref_proof_boundary: z.string(),
    source_ref_reality: z.record(z.unknown()),
  }).passthrough(),
})

export const apiCatalogContextPackResponseSchema = metadataEnvelope.extend({
  data: z.object({
    target: z.string(),
    kind: z.string(),
    key: z.string(),
    summary: z.string(),
    counts: z.record(z.number()),
    endpoints: z.array(z.record(z.unknown())),
    features: z.array(z.record(z.unknown())),
    impacts: z.array(z.record(z.unknown())),
    source_refs: z.array(z.union([z.string(), z.record(z.unknown())])),
    docs_refs: z.array(z.string()),
    warnings: z.array(z.string()),
    source_ref_proof_boundary: z.string(),
    source_ref_reality: z.record(z.unknown()),
    suggested_next_queries: z.array(z.string()),
  }).passthrough(),
})

export const apiCatalogTraceSummaryResponseSchema = metadataEnvelope.extend({
  data: z.object({
    target: z.string(),
    kind: z.string(),
    key: z.string(),
    what_it_is: z.string().optional(),
    affected: z.record(z.number()).optional(),
    what_it_reads: z.array(z.record(z.unknown())),
    what_it_writes: z.array(z.record(z.unknown())),
    what_it_calls: z.array(z.string()).optional(),
    what_can_fail: z.array(z.string()).optional(),
    what_not_to_assume: z.array(z.string()).optional(),
    source_ref_proof_boundary: z.string().optional(),
    source_ref_reality: z.record(z.unknown()).optional(),
    endpoints: z.array(z.record(z.unknown())).optional(),
    suggested_next_queries: z.array(z.string()).optional(),
  }).passthrough(),
})

export const apiCatalogRelationshipsResponseSchema = metadataEnvelope.extend({
  data: z.object({
    target: z.string(),
    kind: z.string(),
    key: z.string(),
    summary: z.string(),
    counts: z.record(z.number()),
    nodes: z.array(z.record(z.unknown())),
    edges: z.array(z.record(z.unknown())),
    source_ref_proof_boundary: z.string(),
    source_ref_reality: z.record(z.unknown()),
    suggested_next_queries: z.array(z.string()),
  }).passthrough(),
})

export const apiCatalogWorkQueueResponseSchema = metadataEnvelope.extend({
  data: z.object({
    issues: z.array(z.record(z.unknown())),
    totals: z.record(z.number()),
    source_ref_proof_boundary: z.string(),
    source_ref_reality: z.record(z.unknown()),
  }).passthrough(),
})

export const apiCatalogVersionsResponseSchema = metadataEnvelope.extend({
  data: z.object({
    current: z.object({
      catalog_version: z.string().min(1),
      source_commit: z.string().min(1),
      generated_from: z.string().min(1),
      strict_mode: z.boolean(),
    }).passthrough(),
  }).passthrough(),
})


export const apiCatalogVersionDiffResponseSchema = metadataEnvelope.extend({
  data: z.object({
    version: z.string().min(1),
    against: z.string().nullable(),
    status: z.string().min(1),
    current_catalog_version: z.string().min(1),
  }).passthrough(),
})

export const apiCatalogOpenApiResponseSchema = z.object({
  openapi: z.literal('3.1.0'),
  info: z.object({
    title: z.string().min(1),
    version: z.string().min(1),
  }).passthrough(),
  paths: z.record(z.unknown()),
  components: z.record(z.unknown()),
  'x-vjal-catalog': z.record(z.unknown()),
}).passthrough()

export type ApiCatalogMetadataReadQuery = z.infer<typeof apiCatalogMetadataReadQuerySchema>
export type ApiCatalogEndpoint = z.infer<typeof apiCatalogEndpointSchema>
export type ApiCatalogSnapshot = z.infer<typeof apiCatalogSnapshotSchema>
export type ApiCatalogCatalogResponse = z.infer<typeof apiCatalogCatalogResponseSchema>
export type ApiCatalogEndpointDetailResponse = z.infer<typeof apiCatalogEndpointDetailResponseSchema>
export type ApiCatalogTableDetailResponse = z.infer<typeof apiCatalogTableDetailResponseSchema>
export type ApiCatalogFieldDetailResponse = z.infer<typeof apiCatalogFieldDetailResponseSchema>
export type ApiCatalogFeatureTraceResponse = z.infer<typeof apiCatalogFeatureTraceResponseSchema>
export type ApiCatalogSearchResponse = z.infer<typeof apiCatalogSearchResponseSchema>
export type ApiCatalogContextPackResponse = z.infer<typeof apiCatalogContextPackResponseSchema>
export type ApiCatalogTraceSummaryResponse = z.infer<typeof apiCatalogTraceSummaryResponseSchema>
export type ApiCatalogRelationshipsResponse = z.infer<typeof apiCatalogRelationshipsResponseSchema>
export type ApiCatalogWorkQueueResponse = z.infer<typeof apiCatalogWorkQueueResponseSchema>
export type ApiCatalogVersionsResponse = z.infer<typeof apiCatalogVersionsResponseSchema>
export type ApiCatalogVersionDiffResponse = z.infer<typeof apiCatalogVersionDiffResponseSchema>
export type ApiCatalogOpenApiResponse = z.infer<typeof apiCatalogOpenApiResponseSchema>
